// Sample Image Utilities for Device Interfaces
// This file provides sample images for different device types and scenarios
// to make interfaces more visually representative and easier to understand

import defaultDashboardImg from '../assets/images/default_dashboard_img.jpg';
import setupImg from '../assets/images/setup.JPG';
import hubImg from '../assets/images/hub.JPG';
import irrigationImg from '../assets/images/irrigation.png';
import humidityImg from '../assets/images/humidity.png';
import temperatureImg from '../assets/images/temperature.png';
import lightImg from '../assets/images/light.png';
import fanImg from '../assets/images/fan.png';
import sensorImg from '../assets/images/sensor.png';
import actionsImg from '../assets/images/actions.png';

// Sample images for different device types
export const DEVICE_SAMPLE_IMAGES = {
  camera: {
    primary: defaultDashboardImg,
    secondary: setupImg,
    tertiary: hubImg,
    thumbnails: [defaultDashboardImg, setupImg, hubImg, defaultDashboardImg]
  },
  irrigation: {
    primary: irrigationImg,
    secondary: defaultDashboardImg,
    thumbnails: [irrigationImg, defaultDashboardImg, irrigationImg]
  },
  temperature: {
    primary: temperatureImg,
    secondary: defaultDashboardImg,
    thumbnails: [temperatureImg, defaultDashboardImg, temperatureImg]
  },
  humidity: {
    primary: humidityImg,
    secondary: defaultDashboardImg,
    thumbnails: [humidityImg, defaultDashboardImg, humidityImg]
  },
  light: {
    primary: lightImg,
    secondary: defaultDashboardImg,
    thumbnails: [lightImg, defaultDashboardImg, lightImg]
  },
  fan: {
    primary: fanImg,
    secondary: defaultDashboardImg,
    thumbnails: [fanImg, defaultDashboardImg, fanImg]
  },
  sensor: {
    primary: sensorImg,
    secondary: defaultDashboardImg,
    thumbnails: [sensorImg, defaultDashboardImg, sensorImg]
  }
};

// Get sample images for a specific device type
export const getSampleImagesForDevice = (deviceType) => {
  return DEVICE_SAMPLE_IMAGES[deviceType] || DEVICE_SAMPLE_IMAGES.camera;
};

// Get a random sample image for variety
export const getRandomSampleImage = (deviceType) => {
  const images = getSampleImagesForDevice(deviceType);
  const imageKeys = Object.keys(images).filter(key => key !== 'thumbnails');
  const randomKey = imageKeys[Math.floor(Math.random() * imageKeys.length)];
  return images[randomKey];
};

// Get sample thumbnails for timeline views
export const getSampleThumbnails = (deviceType, count = 4) => {
  const images = getSampleImagesForDevice(deviceType);
  const thumbnails = images.thumbnails || [images.primary];
  
  // Repeat thumbnails if we need more than available
  const result = [];
  for (let i = 0; i < count; i++) {
    result.push(thumbnails[i % thumbnails.length]);
  }
  return result;
};

// Get sample sensor data for different device types
export const getSampleSensorData = (deviceType) => {
  const baseData = {
    temperature: 72 + Math.floor(Math.random() * 10),
    humidity: 60 + Math.floor(Math.random() * 20),
    light: 400 + Math.floor(Math.random() * 200),
    water: 80 + Math.floor(Math.random() * 20)
  };

  // Adjust based on device type
  switch (deviceType) {
    case 'temperature':
      baseData.temperature = 65 + Math.floor(Math.random() * 30);
      break;
    case 'humidity':
      baseData.humidity = 40 + Math.floor(Math.random() * 40);
      break;
    case 'light':
      baseData.light = 200 + Math.floor(Math.random() * 800);
      break;
    case 'irrigation':
      baseData.water = 70 + Math.floor(Math.random() * 30);
      break;
    case 'fan':
      baseData.humidity = 50 + Math.floor(Math.random() * 30);
      break;
    case 'camera':
      // Camera uses light sensor for triggering
      baseData.light = 300 + Math.floor(Math.random() * 400);
      break;
  }

  return baseData;
};

// Generate sample snapshots with realistic timestamps
export const generateSampleSnapshots = (deviceType, count = 4) => {
  const thumbnails = getSampleThumbnails(deviceType, count);
  const snapshots = [];

  for (let i = 0; i < count; i++) {
    const timestamp = new Date(Date.now() - (i * 24 * 60 * 60 * 1000));
    snapshots.push({
      id: `snapshot_${String(i + 1).padStart(3, '0')}`,
      timestamp: timestamp.toISOString(),
      imageUrl: thumbnails[i],
      thumbnailUrl: thumbnails[i],
      sensorData: getSampleSensorData(deviceType)
    });
  }

  return snapshots;
};

// Default export for convenience
export default {
  DEVICE_SAMPLE_IMAGES,
  getSampleImagesForDevice,
  getRandomSampleImage,
  getSampleThumbnails,
  getSampleSensorData,
  generateSampleSnapshots
};
